import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import GUI from 'lil-gui';

export const EBallActions = {
    // BASIC MOVEMENT
    MOVE_FORWARD: 'MOVE_FORWARD',
    MOVE_BACKWARD: 'MOVE_BACKWARD',
    TURN_LEFT: 'TURN_LEFT',
    TURN_RIGHT: 'TURN_RIGHT',
    
    // DIAGONAL MOVEMENT
    MOVE_FORWARD_LEFT: 'MOVE_FORWARD_LEFT',
    MOVE_FORWARD_RIGHT: 'MOVE_FORWARD_RIGHT',
    MOVE_BACKWARD_LEFT: 'MOVE_BACKWARD_LEFT',
    MOVE_BACKWARD_RIGHT: 'MOVE_BACKWARD_RIGHT',

    JUMP: 'JUMP',
};

export default class Ball {
    MAX_FORCE = 500;
    JUMP_FORCE = 20;

    MAX_SPEED = 16;
    INITIAL_SPEED = 5;

    constructor(scene, world, position) {
        this.scene = scene;
        this.world = world;
        this.position = position;
        this.jumpThreshold = 0.02; // Threshold for considering the ball on the ground

        this.isRemote = false; // Indica si es una bola de otro jugador
        this.targetPosition = position.clone();
        this.interpolationSpeed = 5; // Ajusta la velocidad de interpolación

        this.state = {
            isJumping: false,
            actionCallCount: {
                [EBallActions.MOVE_FORWARD]: 0,
                [EBallActions.MOVE_BACKWARD]: 0,
                [EBallActions.TURN_LEFT]: 0,
                [EBallActions.TURN_RIGHT]: 0,
            }
        };

        this.velocity = new THREE.Vector3();

        // this.setupDebugGUI();
        this.lastUpdateTime = Date.now();
    }

    setupDebugGUI() {
        this.gui = new GUI();
        this.debugFolder = this.gui.addFolder('Ball Debug');
        this.debugData = {
            speed: 0,
            position: { x: 0, y: 0, z: 0 },
        };
        this.debugFolder.add(this.debugData, 'speed').listen();
        this.debugFolder.add(this.debugData.position, 'x').listen();
        this.debugFolder.add(this.debugData.position, 'y').listen();
        this.debugFolder.add(this.debugData.position, 'z').listen();
    }

    getPosition() {
        return this.ballBody.position;
    }

    build() {
        // Crear la geometría y el material del Mesh
        const geometry = new THREE.SphereGeometry(1, 32, 32); // Puedes cambiar la geometría si lo deseas
        const material = new THREE.MeshStandardMaterial({ color: this.isRemote ? 0x0000ff : 0xff4500 });
        this.ballMesh = new THREE.Mesh(geometry, material);
        this.ballMesh.castShadow = true;
        this.ballMesh.receiveShadow = true;
        this.ballMesh.position.copy(this.position);
        this.scene.add(this.ballMesh);

        // Crear el cuerpo físico en Cannon.js
        const shape = new CANNON.Sphere(1);
        this.ballBody = new CANNON.Body({ 
            mass: this.isRemote ? 0 : 1, // Masa cero para jugadores remotos
            shape: shape,
            position: new CANNON.Vec3(this.position.x, this.position.y, this.position.z),
            linearDamping: 1,
            angularDamping: 0.3,
        });

        // Ajustar propiedades físicas
        this.ballBody.linearDamping = 0.7;
        this.ballBody.angularDamping = 0.3;

        // Ajustar restitución (rebote) y fricción
        const ballMaterial = new CANNON.Material('ballMaterial');
        ballMaterial.restitution = 0.1;
        ballMaterial.friction = 0.5;

        this.ballBody.material = ballMaterial;
        this.world.addBody(this.ballBody);
    }

    jump() {
        this.state.isJumping = true;
        this.ballBody.applyImpulse(new CANNON.Vec3(0, this.JUMP_FORCE, 0), this.ballBody.position);
    }

    applyImpulse(impulseX, impulseY, impulseZ) {
        this.ballBody.applyImpulse(new CANNON.Vec3(impulseX, impulseY, impulseZ), this.ballBody.position);

        this.limitSpeed();
    }

    setVelocity(x, y, z) {
        this.ballBody.velocity.set(x, y, z);
    }
    
    applyForce(x, y, z) {
        this.ballBody.applyForce(new CANNON.Vec3(x, y, z), this.ballBody.position);

        this.limitSpeed();
    }

    limitSpeed() {
        const velocity = this.ballBody.velocity;
        const speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2);

        if (speed > this.MAX_SPEED) {
            const factor = this.MAX_SPEED / speed;
            this.ballBody.velocity.x *= factor;
            this.ballBody.velocity.y *= factor;
            this.ballBody.velocity.z *= factor;
        }
    }

    update(deltaTime) {
        if (this.isRemote) {
            // Actualiza la posición del cuerpo físico del jugador remoto
            this.ballBody.position.copy(this.targetPosition);

            // Sincroniza el Mesh con el cuerpo físico
            this.ballMesh.position.copy(this.ballBody.position);
            this.ballMesh.quaternion.copy(this.ballBody.quaternion);
        } else {
            // Sincroniza el Mesh con el cuerpo físico
            this.ballMesh.position.copy(this.ballBody.position);
            this.ballMesh.quaternion.copy(this.ballBody.quaternion);
        }

        // Actualiza la información de depuración y el estado de salto
        // this.updateDebugInfo();
        this.checkJumpStatus();
    }

    checkJumpStatus() {
        if (this.state.isJumping) {
            const verticalVelocity = Math.abs(this.ballBody.velocity.y);
            const heightAboveGround = this.ballBody.position.y - 1.0; // Ajusta según el radio de tu esfera

            if (verticalVelocity < this.jumpThreshold && heightAboveGround < this.jumpThreshold) {
                this.state.isJumping = false;
                console.log("[DEBUG] Jump finished");
            }
        }
    }

    /* updateDebugInfo() {
        const velocity = this.ballBody.velocity;
        this.debugData.speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2);
        this.debugData.position.x = this.ballBody.position.x;
        this.debugData.position.y = this.ballBody.position.y;
        this.debugData.position.z = this.ballBody.position.z;
    } */

    reset(position) {
        this.ballBody.position.set(position.x, position.y, position.z);
        this.ballBody.velocity.set(0, 0, 0);
        this.ballBody.angularVelocity.set(0, 0, 0);
        this.ballBody.wakeUp();
    }

    setPosition(x, y, z, velocity = new THREE.Vector3()) {
        if (this.isRemote) {
            this.targetPosition.set(x, y, z);
            this.velocity.copy(velocity);
        } else {
            // Para la bola local, actualiza el cuerpo físico
            this.ballBody.position.set(x, y, z);
            this.ballBody.velocity.set(0, 0, 0);
            this.ballBody.angularVelocity.set(0, 0, 0);
            this.ballBody.wakeUp();
            this.ballMesh.position.copy(this.ballBody.position);
            // this.updateDebugInfo();
            console.log(`[DEBUG] Ball position set to (${x}, ${y}, ${z})`);
        }
    }
}
